import { useMusicalSelector } from "../TrainingConfiguration";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import NotePlayer from "./NotePlayer";
import { useDispatch, useSelector } from "react-redux";
import classes from "./EarTrainerPage.module.css";
import EarTrainerSetup from "./EarTrainerSetup/EarTrainerSetup";
import ChartDataToSVG from "../code/ChartDataToSVG";
import ChartEditor from "../CreateScalePage/ChartEditor/ChartEditor";
import EarTrainerChart from "./EarTrainerChart.jsx";
import EarTrainerQuickTour from "./EarTrainerQuickTour";
import EarTrainerHelp from "./EarTrainerHelp";
import { mainActions } from "../../store";
import Card from "../UI/Card";
import DronePlayer from "./DronePlayer";
import Button from "../UI/Button";
import RequestSupportCard from "../UI/RequestSupportCard";
import BottomMenu from "../BottomMenu/BottomMenu";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { trackPracticeNote } from "../../privacy/analytics";
import { buildTrainingModel, chooseQuestion, generateTrainerCharts, intervalNames,
  isCorrectPitch, validChartIndex } from "./trainingModel";

export const ANSWER_CONFIRMATION_MS = 500;
// Keep colour choices out of shape de-duplication and session identity. Apply
// display colours only when drawing, using the existing default geometry.
const geometryColors = ["#000000", ...Array(11).fill("#ffffff")];

const EarTrainerPage = () => {
  const dispatch = useDispatch();
  const tuning = useMusicalSelector(state => state.tuning);
  const intervals = useMusicalSelector(state => state.intervals);
  const intervalColors = useMusicalSelector(state => state.intervalColors);
  const selectNps = useMusicalSelector(state => state.selectNps);
  const midiRootNote = useMusicalSelector(state => state.midiRootNote);
  const earTrainerChart = useSelector(state => state.earTrainerChart);
  const earTrainerQuestionType = useSelector(state => state.earTrainerQuestionType);
  const earTrainerShowNotes = useSelector(state => state.earTrainerShowNotes);
  const earTrainerMode = useSelector(state => state.earTrainerMode);
  const earTrainerShapeChange = useSelector(state => state.earTrainerShapeChange);
  const earTrainerRootNote = useSelector(state => state.earTrainerRootNote);
  const earTrainerRootOctaveSelect = useSelector(state => state.earTrainerRootOctaveSelect);
  const earTrainerIgnoreOctaves = useSelector(state => state.earTrainerIgnoreOctaves);

  // Derive one current snapshot for the dropdown, image and questions. Waiting
  // for RenderData's separate effect would briefly expose the previous scale.
  const charts = useMemo(() => generateTrainerCharts(tuning, intervals, geometryColors, selectNps),
    [tuning, intervals, selectNps]);
  const selectedIndex = validChartIndex(earTrainerChart, charts.length);
  const model = useMemo(() => buildTrainingModel(charts[selectedIndex], tuning, intervals,
    earTrainerRootNote, midiRootNote, earTrainerRootOctaveSelect),
  [charts, selectedIndex, tuning, intervals, earTrainerRootNote, midiRootNote, earTrainerRootOctaveSelect]);
  const svgData = useMemo(() => {
    if (!model.position) return { image: "" };
    let offset = model.position.notesOffset;
    const colored = model.position.chartData.map((row, string) => {
      const notes = row.map(([fret]) => [fret, intervalColors[(offset + fret) % 12]]);
      offset += tuning[string] || 0;
      return notes;
    });
    return ChartDataToSVG(colored, model.position.maxFret, earTrainerShowNotes,
      { notesOffset: model.position.notesOffset, tuning });
  }, [model.position, earTrainerShowNotes, intervalColors, tuning]);

  const [session, setSession] = useState(null);
  const [restart, setRestart] = useState(0);
  const audio = useRef(null);
  const [audioState, setAudioState] = useState({ status: "loading", message: "Preparing Ear Trainer audio…" });
  const [starting, setStarting] = useState(false);
  const gesturePending = useRef(false);
  const interactionGeneration = useRef(0);
  const mounted = useRef(false);
  const [lastPlayedInterval, setLastPlayedInterval] = useState();
  const [shiftPressed, setShiftPressed] = useState(false);
  const rootTimeoutIdRef = useRef(null);
  const confirmationTimeout = useRef(null);
  const pendingConfirmation = useRef(null);
  const previousPitch = useRef(0);
  const shapeChangeCount = useRef(0);
  const handledTransition = useRef(null);
  const activeSession = session?.model === model ? session : null;
  const configurationError = model.error || (activeSession?.phase === "unavailable"
    ? "Choose a diagram with at least two different playable pitches for training." : null);

  useEffect(() => {
    if (earTrainerChart !== selectedIndex) dispatch(mainActions.setEarTrainerChart(selectedIndex));
  }, [earTrainerChart, selectedIndex, dispatch]);

  useEffect(() => {
    const handleKeyDown = event => { if (event.key === "Shift") setShiftPressed(true); };
    const handleKeyUp = event => { if (event.key === "Shift") setShiftPressed(false); };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const play = useCallback(pitch => audio.current?.play(pitch), []);
  const onAudioStateChange = useCallback(state => {
    setAudioState(state);
    if (state.status !== "ready") {
      dispatch(mainActions.setEarTrainerMode(0));
      setSession(null);
    }
  }, [dispatch]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    interactionGeneration.current++;
    gesturePending.current = false;
    setStarting(false);
  }, [model, earTrainerMode]);

  const withAudio = action => {
    if (audioState.status !== "ready" || gesturePending.current) return;
    const generation = interactionGeneration.current;
    const result = audio.current?.activate();
    if (result === true) { action(); return; }
    if (result?.then) {
      gesturePending.current = true;
      setStarting(true);
      result.then(ok => {
        if (!mounted.current || interactionGeneration.current !== generation) return;
        gesturePending.current = false;
        setStarting(false);
        if (ok) action();
      });
    }
  };
  const onStart = () => withAudio(() => dispatch(mainActions.setEarTrainerMode(1)));

  // Every new configuration owns its root-to-target timeout. A changed scale,
  // invalid setup, Stop or unmount cancels the old transition before it can play.
  useEffect(() => {
    clearTimeout(confirmationTimeout.current);
    pendingConfirmation.current = null;
    if (!earTrainerMode || model.error) {
      setSession(null);
      return;
    }
    if (!play(model.rootNote)) { setSession(null); return; }
    setSession({ model, phase: "root", question: null });
    rootTimeoutIdRef.current = setTimeout(() => {
      setSession({ model, phase: "next", question: null });
      rootTimeoutIdRef.current = null;
    }, 1000);
    return () => {
      clearTimeout(rootTimeoutIdRef.current);
      clearTimeout(confirmationTimeout.current);
      pendingConfirmation.current = null;
    };
  }, [model, earTrainerMode, play, restart]);

  useEffect(() => {
    if (!earTrainerMode || model.error || session?.model !== model || session.phase !== "next" ||
        handledTransition.current === session) return;
    handledTransition.current = session;
    if (earTrainerShapeChange && shapeChangeCount.current >= earTrainerShapeChange) {
      shapeChangeCount.current = 0;
      dispatch(mainActions.setEarTrainerChart(Math.floor(Math.random() * charts.length)));
      dispatch(mainActions.setEarTrainerRootNote(Math.floor(Math.random() * 12) + 40));
      // The randomly selected settings can equal the old ones: still replay the
      // reference for the new batch. No dispatches occur inside state updaters.
      setRestart(value => value + 1);
      return;
    }
    const question = chooseQuestion(model.questions, previousPitch.current);
    if (!question) {
      setSession({ model, phase: "unavailable", question: null });
      return;
    }
    if (!play(question.pitch + earTrainerRootNote + model.octave)) return;
    previousPitch.current = question.pitch;
    if (earTrainerShapeChange) shapeChangeCount.current += 1;
    setSession({ model, phase: "wait", question, announcement: session.announcement });
  }, [session, model, earTrainerMode, earTrainerShapeChange, charts.length, dispatch, play, earTrainerRootNote]);

  const handleNoteClick = (id, string, fret, shift) => {
    if (!model.position) return;
    // Lock before React renders: repeated taps must not queue transitions or
    // replace the green confirmation with another attempt's feedback.
    if (earTrainerMode && (pendingConfirmation.current || activeSession?.phase === "correct")) return;
    trackPracticeNote();
    let pitch = fret + model.position.notesOffset;
    for (let n = 0; n < string; n++) pitch += tuning[n];
    if (shift || shiftPressed || earTrainerMode === 0) {
      if (audioState.status !== "ready") return;
      withAudio(() => {
        if (play(pitch + earTrainerRootNote + model.octave)) setLastPlayedInterval(intervalNames[pitch % 12]);
      });
      return "audition";
    } else if (!configurationError && activeSession?.phase === "wait") {
      setLastPlayedInterval(intervalNames[pitch % 12]);
      if (!isCorrectPitch(pitch, activeSession.question.pitch, earTrainerIgnoreOctaves)) {
        setSession({ ...activeSession, feedback: "incorrect", announcement: "incorrect" });
        return "incorrect";
      }
      const confirmation = { model, session: activeSession };
      pendingConfirmation.current = confirmation;
      setSession({ ...activeSession, phase: "correct", feedback: "correct" });
      confirmationTimeout.current = setTimeout(() => {
        if (pendingConfirmation.current !== confirmation) return;
        pendingConfirmation.current = null;
        confirmationTimeout.current = null;
        setSession({ ...activeSession, phase: "next", feedback: null, announcement: "correct" });
      }, ANSWER_CONFIRMATION_MS);
      return "correct";
    }
  };

  let questionText = <>&nbsp;</>;
  if (configurationError) questionText = <span role="alert">{configurationError}</span>;
  else if (!earTrainerMode && lastPlayedInterval) questionText = <>Playing the&nbsp;<b>{lastPlayedInterval}</b></>;
  else if (earTrainerMode && activeSession?.phase === "root") questionText = <>Playing the&nbsp;<b>Root</b></>;
  else if (earTrainerMode && activeSession?.question) questionText = earTrainerQuestionType !== 2
    ? <>Can you find the&nbsp;<b>{activeSession.question.interval}</b>?</>
    : <>Can you find the played note?</>;

  const onHearRoot = () => { if (!configurationError) withAudio(() => play(model.rootNote)); };
  const onHearQuestion = () => {
    if (!configurationError && activeSession?.question) {
      withAudio(() => play(activeSession.question.pitch + earTrainerRootNote + model.octave));
    }
  };

  return (
    <div className={classes.background} data-ear-trainer-page>
      <HelmetProvider>
        <Helmet>
          <meta charset="UTF-8" />
          <title>Guitar Ear Trainer | Guitar Charts</title>
          <meta
            name="description"
            content="Practise connecting the pitches you hear with positions on the guitar fretboard. Listen to a root and question note, then find the pitch with adjustable visual hints."
          />
          <link rel="canonical" href="https://guitar-charts.com/training" />
        </Helmet>
      </HelmetProvider>

      <Card>
        <div className={classes.introduction}>
          <h1 className={classes.h1}>Guitar Charts - Ear Trainer</h1>
          <p>Connect the pitches you hear with places you can play them on the guitar fretboard.
            Listen to a root and a question note, then find the question's pitch on the displayed diagram.
            Use this listening practice to explore the connection between your ear and your fingers when improvising.</p>
          <div className={classes.helpLinks}>
            <a href="#ear-trainer-usage">How to use</a>
            <EarTrainerQuickTour />
          </div>
        </div>
      </Card>
      <RequestSupportCard />
      <div className={classes.configuration} data-tour-target="ear-notes">
        <Card>
          <ChartEditor accessibleColors />
        </Card>
        <Card>
          <EarTrainerSetup charts={charts} selectedIndex={selectedIndex}
            trainingUnavailable={!!configurationError} audio={audioState} starting={starting} onStart={onStart}
            onRetry={() => audio.current?.retry()} onResume={() => audio.current?.activate()} />
        </Card>
      </div>
      <Card>
        <table className={classes.practice}>
          <tbody>
            <tr><td colSpan={2} style={{ textAlign: "center" }}>
              {earTrainerMode
                ? "Listen to the root and question; select the pitch you heard anywhere on the fretboard. Correct advances automatically; otherwise try again. Replay either note below."
                : "Click or tap any fretboard position to hear its note."}
              </td></tr>
            <tr>
              <td colSpan={2} style={{ textAlign: "center" }}>
                <div className={classes.questionLine}>
                  <span>{questionText}</span>
                  <span role="status" aria-live="polite" aria-atomic="true"
                    className={!activeSession?.feedback ? classes.srOnly
                      : activeSession.feedback === "correct" ? classes.correct : classes.incorrect}>
                    {!configurationError && !!earTrainerMode && ((activeSession?.feedback || activeSession?.announcement) === "correct"
                      ? "Correct" : (activeSession?.feedback || activeSession?.announcement) === "incorrect" ? "Try again" : "")}
                  </span>
                </div>
              </td>
            </tr>
            {earTrainerMode ? (
              <tr>
                <td>
                  <Button onClick={onHearRoot} disabled={!!configurationError || audioState.status !== "ready"}>Hear The Root</Button>
                </td>
                <td>
                  <Button onClick={onHearQuestion} disabled={!!configurationError || !activeSession?.question || audioState.status !== "ready"}>Hear The Question</Button>
                </td>
              </tr>
            ) : (
              <tr></tr>
            )}
          </tbody>
        </table>
      </Card>
      <Card>
        <div className={classes.imageGrid} data-tour-target="ear-fretboard">
          {svgData.image && <EarTrainerChart
            index={0}
            image={svgData.image}
            onClick={handleNoteClick}
            stringCount={svgData.strings}
            maxFret={svgData.frets}
            feedbackContext={model}
            trainingMode={!!earTrainerMode}
            confirmationDuration={ANSWER_CONFIRMATION_MS}
          />}
        </div>
      </Card>
      <Card>
        <EarTrainerHelp />
      </Card>
      <Card>
        <BottomMenu />
      </Card>
      <NotePlayer ref={audio} onAudioStateChange={onAudioStateChange} />
      <DronePlayer note={0} keydown={false} />

      {/* put some space in the bottom of the chart so you can scroll the ear trainer chart into view */}
      <div style={{ height: "100vh" }} />
    </div>
  );
};

export default EarTrainerPage;
